import { useCallback, useRef } from 'react';

import { useCurrentUser } from '@/entities/user/currentUser';

import { nextBannerCtaIdx } from '../lib/bannerCta';
import { useLeadSurveyFlowStore } from './useLeadSurveyFlowStore';

// Lifetime of the persisted survey-bypass escape hatch. The bypass is a temporary reprieve from a
// failing lead-POST, not a permanent exemption: after this window the forced survey is shown again, so
// a lead whose backend has since recovered is re-prompted instead of being suppressed forever.
const SURVEY_BYPASS_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Flow-state spine for the Trial Lead Survey.
 *
 * Truth split:
 * - `isSurveyPending` / `isQualified` derive from the backend-authoritative user fields
 *   (`lead_survey_completed` / `lead_survey_qualified`). No clientStorage mirror — the user object is
 *   already loaded at app init, so there is no flash, and the backend can never silently diverge.
 * - `callState` ('idle' | 'booked' | 'skipped', `booked` terminal) and the banner CTA rotation index
 *   are purely client-side and persist per-device in localStorage via `useLeadSurveyFlowStore`.
 */
export const useLeadSurvey = () => {
  const { data: user, isLoading: isUserLoading } = useCurrentUser();

  const callState = useLeadSurveyFlowStore((state) => state.callState);
  const bannerCtaIdx = useLeadSurveyFlowStore((state) => state.bannerCtaIdx);
  const surveyBypassedAt = useLeadSurveyFlowStore((state) => state.surveyBypassedAt);
  const setCallState = useLeadSurveyFlowStore((state) => state.setCallState);
  const setBannerCtaIdx = useLeadSurveyFlowStore((state) => state.setBannerCtaIdx);
  const setSurveyBypassedAt = useLeadSurveyFlowStore((state) => state.setSurveyBypassedAt);

  const isQualified = user?.lead_survey_qualified === true;
  // Raw backend-derived flag — consumers MUST combine it with the eligibility gate (trial plan +
  // target locale). Resolved-but-undefined means not completed → pending; wait for the query (FOUC).
  const isSurveyPending = !isUserLoading && user?.lead_survey_completed !== true;

  // Single source of truth for the retargeting banner: a qualified lead who skipped the call.
  const shouldShowBookADemoBanner = isQualified && callState === 'skipped';

  // Derived from the persisted timestamp: the bypass is in effect only within the TTL window, so it
  // recovers automatically once the window elapses (see SURVEY_BYPASS_TTL_MS).
  const surveyBypassed = surveyBypassedAt !== null && Date.now() - surveyBypassedAt < SURVEY_BYPASS_TTL_MS;

  const markBooked = useCallback(() => setCallState('booked'), [setCallState]);

  // Persisted escape hatch: records WHEN the user bypassed the forced survey so it stays bypassed
  // across reloads (a failing lead-POST can't re-trap them) but only until the TTL expires.
  const markSurveyBypassed = useCallback(() => setSurveyBypassedAt(Date.now()), [setSurveyBypassedAt]);

  // 'booked' is terminal — never override a booked state with 'skipped'.
  const markSkipped = useCallback(() => {
    if (callState !== 'booked') {
      setCallState('skipped');
    }
  }, [callState, setCallState]);

  // Advance the rotation index once per mount; the ref survives StrictMode's double-invoke.
  const advancedRef = useRef(false);
  const nextBannerCta = useCallback(() => {
    if (!advancedRef.current) {
      advancedRef.current = true;
      setBannerCtaIdx(nextBannerCtaIdx(bannerCtaIdx));
    }

    return bannerCtaIdx;
  }, [bannerCtaIdx, setBannerCtaIdx]);

  return {
    callState,
    isQualified,
    isSurveyPending,
    markBooked,
    markSkipped,
    markSurveyBypassed,
    nextBannerCta,
    shouldShowBookADemoBanner,
    surveyBypassed,
  };
};
